import React, { useState } from 'react';
import { Filter, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * StudyFilterPanel — Painel de filtros colapsável para estudos
 *
 * Props:
 *   filters       — Array de { key, label, value, options: [{value, label}], onChange }
 *   onClear       — Callback para limpar todos os filtros
 *   hasActiveFilters — Boolean indicando se algum filtro está ativo
 *   defaultOpen   — Painel aberto por padrão? (default: true)
 */
const StudyFilterPanel = ({
  filters = [],
  onClear,
  hasActiveFilters = false,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  if (!filters.length) return null;

  // Determine grid columns based on filter count
  const gridCols = filters.length <= 2
    ? 'grid-cols-1 sm:grid-cols-2'
    : filters.length === 3
      ? 'grid-cols-1 sm:grid-cols-3'
      : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="font-bold text-gray-900">Filtros</span>
          {hasActiveFilters && (
            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
              Ativo
            </span>
          )}
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {open && (
        <div className={`px-6 pb-6 grid ${gridCols} gap-4 border-t border-gray-100 pt-4`}>
          {filters.map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">
                {f.label}
              </label>
              <select
                value={f.value}
                onChange={(e) => f.onChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-gray-200 focus:outline-none"
              >
                <option value="">{f.placeholder || `Todos`}</option>
                {(f.options || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ))}

          {/* Clear filters */}
          {hasActiveFilters && onClear && (
            <div className={`sm:col-span-2 lg:col-span-${Math.min(filters.length, 4)} flex justify-end items-end`}>
              <button
                onClick={onClear}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Limpar filtros
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudyFilterPanel;
